// 流入元 referrer の host 部分を first-touch で localStorage に保存し、
// 登録完了後の最初のログイン済みリクエストで一度だけ自社APIへ送る。
// 送信成功後は localStorage を消す（再送防止）。
//
// 計測URL（/r/:slug）の Cookie 同期と並ぶ取りこぼし対策。
// Form 経由ではなく LINE OmniAuth フローを通すため、Cookie ではなく
// localStorage に持つことで OAuth 経由でも値を維持する。

import type { AxiosInstance } from "axios";

const STORAGE_KEY = "referrer_domain";
const SUBMITTED_KEY = "referrer_domain_submitted";

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function safeLocalStorage(): Storage | null {
  if (!isBrowser()) return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function extractHost(rawReferrer: string): string | null {
  try {
    const url = new URL(rawReferrer);
    return url.host || null;
  } catch {
    return null;
  }
}

// LP 等の着地時に呼ぶ。document.referrer の host が同一オリジン以外なら
// first-touch のみ localStorage に保存する。既に値があれば上書きしない。
export function captureReferrerDomain(): void {
  const storage = safeLocalStorage();
  if (!storage) return;
  if (storage.getItem(STORAGE_KEY)) return;
  if (storage.getItem(SUBMITTED_KEY)) return;

  const referrer = document.referrer;
  if (!referrer) return;

  const host = extractHost(referrer);
  if (!host) return;
  if (host === window.location.host) return;

  storage.setItem(STORAGE_KEY, host.toLowerCase());
}

// ログイン済みページで呼ぶ。localStorage に値があれば API へ送って消す。
// サーバ側も先行 return で idempotent なので、複数回呼ばれても害は無い。
export async function flushReferrerDomain(client: AxiosInstance): Promise<void> {
  const storage = safeLocalStorage();
  if (!storage) return;

  const domain = storage.getItem(STORAGE_KEY);
  if (!domain) return;

  try {
    await client.post("/users/referrer_domain", { domain });
    storage.removeItem(STORAGE_KEY);
    storage.setItem(SUBMITTED_KEY, "1");
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    // 422（正規化失敗 etc）は永続失敗なので破棄。401（未ログイン）は次回ログイン時に再試行
    if (status === 422) {
      storage.removeItem(STORAGE_KEY);
    }
  }
}
